import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  DirectionsRun as FitnessIcon,
  EmojiEvents as SkillIcon,
  ExpandMore as ExpandMoreIcon,
  FitnessCenter as DrillIcon,
  Insights as MatchIntelIcon,
  Psychology as TacticalIcon,
  Notes as NotesIcon,
  PlayCircleOutline as PlayCircleOutlineIcon,
  Save as SaveIcon,
  SmartDisplay as SmartDisplayIcon,
  Upload as UploadIcon,
} from '@mui/icons-material';
import { resolveContestVideoSrc } from '@utils/contestSubmissionStatus';
import { brandColors, brandGradients, getGradeColor, getGradeTierLabel } from '@config/brandTokens';
import GlossyActionButton from '@components/ui/GlossyActionButton';
import { ContestAgeBadge } from '@components/contest/ContestAgeBadge';
import { Link as RouterLink } from 'react-router-dom';
import type { ContestCatalogEntry, ContestCategoryId, ContestGenderKey, ContestGradeCode, ContestMediaItem, ContestSectionKey } from '../../types/contestCatalog';
import type { ContestCategoryGroup } from '@utils/contestCatalog';
import {
  CONTEST_SECTION_ORDER,
  formatContestVideoNumber,
  getGradeBandsForAge,
  getBenchmarkUnit,
  getTargetTimeDisplay,
  resolveScorecardGradeCodes,
  getMediaForSection,
  getOrderedReferenceVideos,
  resolveMediaClipForAge,
  type OrderedContestVideo,
  getSectionByKey,
  getSectionContentForAge,
  getVisibleFrame2Sections,
  getFrame2SectionVisibleIndex,
} from '@utils/contestCatalog';
import { ContestSkillBenchmarkBlock } from '@components/contest/ContestSkillBenchmarkBlock';
import { ContestSectionBlocksView } from '@components/contest/ContestSectionBlocks';
import CapabilityLevelChip from '@components/contest/CapabilityLevelChip';
import { getCategoryRowStyle } from '@components/contest/contestCategoryTheme';

interface CategoryTheme {
  label: string;
  accent: string;
  headerBg: string;
  headerText: string;
  rowBg: string;
  rowBorder: string;
  rowHover: string;
  rowText: string;
  rowTarget: string;
  selectedBg: string;
  selectedBorder: string;
  selectedTarget: string;
  icon: React.ReactElement;
}

const CATEGORY_THEME: Record<ContestCategoryId, CategoryTheme> = {
  skill: {
    label: 'Skill',
    accent: brandColors.blue,
    headerBg: brandGradients.contestHeader,
    headerText: brandColors.white,
    rowBg: '#eef2ff',
    rowBorder: '#a5b4fc',
    rowHover: '#e0e7ff',
    rowText: brandColors.navy,
    rowTarget: brandColors.blue,
    selectedBg: brandColors.navy,
    selectedBorder: brandColors.blue,
    selectedTarget: '#a5b4fc',
    icon: <SkillIcon />,
  },
  drill: {
    label: 'Drill',
    accent: brandColors.violet,
    headerBg: `linear-gradient(135deg, #3d0f6b 0%, ${brandColors.violet} 55%, #8b3fd9 100%)`,
    headerText: brandColors.white,
    rowBg: '#f3e8ff',
    rowBorder: '#c4b5fd',
    rowHover: '#ede9fe',
    rowText: '#4c1d95',
    rowTarget: brandColors.violet,
    selectedBg: '#4c1d95',
    selectedBorder: '#a78bfa',
    selectedTarget: '#c4b5fd',
    icon: <DrillIcon />,
  },
  fitness: {
    label: 'Fitness',
    accent: brandColors.green,
    headerBg: `linear-gradient(135deg, #1a5c10 0%, ${brandColors.green} 55%, #6fd04a 100%)`,
    headerText: '#ffffff',
    rowBg: '#d1fae5',
    rowBorder: '#6ee7b7',
    rowHover: '#a7f3d0',
    rowText: '#065f46',
    rowTarget: '#059669',
    selectedBg: '#065f46',
    selectedBorder: '#34d399',
    selectedTarget: '#6ee7b7',
    icon: <FitnessIcon />,
  },
  tactical: {
    label: 'Tactical',
    accent: '#f59e0b',
    headerBg: 'linear-gradient(135deg, #78350f 0%, #d97706 55%, #fbbf24 100%)',
    headerText: '#ffffff',
    rowBg: '#fef3c7',
    rowBorder: '#fcd34d',
    rowHover: '#fde68a',
    rowText: '#92400e',
    rowTarget: '#d97706',
    selectedBg: '#92400e',
    selectedBorder: '#fbbf24',
    selectedTarget: '#fcd34d',
    icon: <TacticalIcon />,
  },
  match_intelligence: {
    label: 'Match Intelligence',
    accent: '#ec4899',
    headerBg: 'linear-gradient(135deg, #831843 0%, #db2777 55%, #f472b6 100%)',
    headerText: '#ffffff',
    rowBg: '#fce7f3',
    rowBorder: '#f9a8d4',
    rowHover: '#fbcfe8',
    rowText: '#9d174d',
    rowTarget: '#db2777',
    selectedBg: '#9d174d',
    selectedBorder: '#f9a8d4',
    selectedTarget: '#fbcfe8',
    icon: <MatchIntelIcon />,
  },
};

const getYouTubeEmbedId = (url: string): string | null => {
  const patterns = [
    /youtube\.com\/shorts\/([^&\n?#/]+)/,
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
  ];
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match?.[1]) return match[1];
  }
  return null;
};

export const ContestEmbeddedVideo: React.FC<{ url: string; title?: string; fill?: boolean }> = ({
  url,
  title,
  fill = false,
}) => {
  const videoId = getYouTubeEmbedId(url);
  if (!videoId) {
    return (
      <Box
        sx={{
          p: 2,
          bgcolor: '#1e293b',
          color: '#94a3b8',
          minHeight: fill ? '100%' : 160,
          height: fill ? '100%' : undefined,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography variant="body2">Video preview unavailable.</Typography>
      </Box>
    );
  }

  if (fill) {
    return (
      <Box sx={{ position: 'absolute', inset: 0, bgcolor: '#000' }}>
        <Box
          component="iframe"
          title={title || 'Contest video'}
          src={`https://www.youtube.com/embed/${videoId}`}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          sx={{ width: '100%', height: '100%', border: 0 }}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', paddingBottom: '56.25%', height: 0, bgcolor: '#000' }}>
      <Box
        component="iframe"
        title={title || 'Contest video'}
        src={`https://www.youtube.com/embed/${videoId}`}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
      />
    </Box>
  );
};

interface FrameHeaderProps {
  label: string;
  accent?: string;
  /** Primary = contest board title (larger, navy gradient) */
  emphasis?: boolean;
  /** Selected age category shown on every frame header */
  ageCategory?: string;
  icon?: React.ReactNode;
}

const FrameHeader: React.FC<FrameHeaderProps> = ({
  label,
  accent = brandColors.blue,
  emphasis = false,
  ageCategory,
  icon,
}) => {
  const ageChip = ageCategory ? <ContestAgeBadge age={ageCategory} /> : null;

  const headerIcon = icon ?? (emphasis ? null : <AssignmentIcon sx={{ fontSize: 16, color: accent }} />);

  if (emphasis) {
    return (
      <Box
        sx={{
          px: 1.75,
          py: 1.35,
          background: brandGradients.contestHeader,
          borderBottom: `3px solid ${brandColors.blue}`,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        {icon && (
          <Box sx={{ display: 'flex', alignItems: 'center', color: brandColors.white, '& .MuiSvgIcon-root': { fontSize: 22 } }}>
            {icon}
          </Box>
        )}
        <Typography
          sx={{
            fontWeight: 700,
            letterSpacing: 0.4,
            fontSize: { xs: '1.15rem', md: '1.35rem' },
            lineHeight: 1.15,
            color: brandColors.white,
            fontFamily: '"Sora", Arial, sans-serif',
            flex: 1,
          }}
        >
          {label}
        </Typography>
        <Box sx={{ ml: 'auto' }}>{ageChip}</Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        px: 1.5,
        py: 1.1,
        bgcolor: brandColors.white,
        borderBottom: `2px solid ${accent}`,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
      }}
    >
      {headerIcon}
      <Typography
        variant="caption"
        sx={{
          fontWeight: 700,
          letterSpacing: 1,
          textTransform: 'uppercase',
          color: brandColors.navy,
          fontSize: '0.75rem',
          fontFamily: '"Sora", Arial, sans-serif',
          flex: 1,
        }}
      >
        {label}
      </Typography>
      {ageChip}
    </Box>
  );
};

/** Tight grid: # | status | contest | target time | grade columns… */
const SCORECARD_FONT = '"Sora", Arial, sans-serif';
const SCORECARD_HEADER_FONT = { xs: '0.62rem', sm: '0.68rem' };
const SCORECARD_VALUE_FONT = { xs: '0.58rem', sm: '0.65rem' };
const GRADE_COL_WIDTH = 'minmax(28px, 34px)';
const BENCHMARK_GRADE_COL_WIDTH = 'minmax(78px, 1fr)';
const EVAL_GRADE_COL_WIDTH = 'minmax(64px, 88px)';
const TARGET_COL_WIDTH = 'minmax(54px, 62px)';
const BENCHMARK_TARGET_COL_WIDTH = 'minmax(48px, 56px)';

function buildLearnListGrid(): string {
  return '18px 14px minmax(0, 1fr) 86px';
}

function buildLearnScorecardGrid(gradeCount: number): string {
  const gradeCols = Array(Math.max(gradeCount, 1)).fill(GRADE_COL_WIDTH).join(' ');
  return `18px 14px minmax(0, 1fr) 86px ${TARGET_COL_WIDTH} ${gradeCols}`;
}

/** Benchmark tab — contest + grades stay adjacent (no 1fr stretch pushing grades right). */
function buildBenchmarkScorecardGrid(gradeCount: number): string {
  const gradeCols = Array(Math.max(gradeCount, 1)).fill(BENCHMARK_GRADE_COL_WIDTH).join(' ');
  return `22px minmax(108px, 1fr) 86px ${BENCHMARK_TARGET_COL_WIDTH} ${gradeCols}`;
}

function buildEvalScorecardGrid(gradeCount: number, variant: 'self_eval' | 'contest'): string {
  const gradeCols = Array(Math.max(gradeCount, 1)).fill(EVAL_GRADE_COL_WIDTH).join(' ');
  const tail =
    variant === 'contest'
      ? 'minmax(68px, 80px) 38px minmax(96px, 108px) minmax(96px, 108px) 38px minmax(80px, 1fr) minmax(92px, 104px)'
      : 'minmax(68px, 80px) 38px minmax(96px, 108px) 38px minmax(80px, 1fr) minmax(140px, 180px) minmax(80px, 1fr)';
  return `22px minmax(0, 1fr) 86px ${TARGET_COL_WIDTH} ${gradeCols} ${tail}`;
}

function scorecardGridMinWidth(gradeCount: number, mode: 'learn-list' | 'learn-scorecard' | 'benchmark' | 'eval', variant: 'self_eval' | 'contest'): number {
  if (mode === 'learn-list') return 300;
  if (mode === 'benchmark') return 200 + 86 + 56 + gradeCount * 80 + 48;
  if (mode === 'eval') {
    const gradeW = 0;
    const entryCols = variant === 'contest' ? 600 : 532;
    return 170 + 86 + gradeW + entryCols;
  }
  return 300 + 86 + gradeCount * 34;
}

const EVAL_REF_GRADE_COL_PX = 34;
const EVAL_CONTEST_COL_MIN = 148;
const EVAL_ENTRY_ZONE_BG = '#f8fafc';
const EVAL_REF_ZONE_BG = '#e2e8f0';
const EVAL_REF_ZONE_HEADER = '#cbd5e1';
const EVAL_REF_CELL_BG = '#f1f5f9';
const EVAL_REF_CELL_COLOR = '#64748b';

const evalRefGradeHeaderSx = {
  bgcolor: `${EVAL_REF_ZONE_BG} !important`,
  color: `${EVAL_REF_CELL_COLOR} !important`,
  fontSize: '0.6rem',
  fontWeight: 700,
  px: 0.15,
  py: 0.45,
  width: EVAL_REF_GRADE_COL_PX,
  lineHeight: 1,
  whiteSpace: 'nowrap' as const,
};

const evalRefGradeCellSx = {
  bgcolor: `${EVAL_REF_CELL_BG} !important`,
  color: `${EVAL_REF_CELL_COLOR} !important`,
  fontSize: '0.58rem',
  fontWeight: 500,
  textAlign: 'center' as const,
  px: 0.15,
  py: 0.5,
  verticalAlign: 'middle' as const,
  borderBottom: '1px solid #e2e8f0',
  lineHeight: 1,
  whiteSpace: 'nowrap' as const,
  letterSpacing: '-0.02em',
};

const benchmarkGradeHeaderCellSx = (colors: ReturnType<typeof getGradeColor>, tier: string) => ({
  display: 'flex',
  flexDirection: 'column' as const,
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center' as const,
  fontFamily: SCORECARD_FONT,
  color: colors.text,
  bgcolor: colors.bg,
  border: `1px solid ${colors.border}`,
  borderRadius: 0.75,
  py: 0.35,
  px: 0.25,
  lineHeight: 1.05,
  minWidth: 0,
  minHeight: 36,
});

const benchmarkTargetHeaderCellSx = {
  textAlign: 'center' as const,
  fontWeight: 900,
  fontSize: SCORECARD_HEADER_FONT,
  fontFamily: SCORECARD_FONT,
  color: '#475569',
  bgcolor: '#f1f5f9',
  border: '1px solid #cbd5e1',
  borderRadius: 0.75,
  py: 0.35,
  px: 0.25,
  lineHeight: 1.15,
  minWidth: 0,
  whiteSpace: 'nowrap' as const,
};

const evalCardTableSx = {
  width: '100%',
  borderCollapse: 'collapse' as const,
  tableLayout: 'fixed' as const,
  fontFamily: SCORECARD_FONT,
  fontSize: '0.875rem',
  '& thead th': {
    position: 'sticky' as const,
    top: 0,
    zIndex: 2,
    fontSize: '0.8125rem',
    fontWeight: 700,
    color: brandColors.navy,
    bgcolor: '#f1f5f9',
    borderBottom: '2px solid #cbd5e1',
    px: 1,
    py: 0.85,
    textAlign: 'center' as const,
    verticalAlign: 'middle' as const,
    lineHeight: 1.25,
    boxShadow: '0 1px 0 #cbd5e1',
  },
  '& tbody td': {
    px: 1,
    py: 0.75,
    verticalAlign: 'middle' as const,
    borderBottom: '1px solid #e2e8f0',
    fontSize: '0.875rem',
  },
};

function evalCategoryHeaderRow(colSpan: number, categoryId: ContestCategoryId, withTopBorder = false) {
  const theme = getCategoryRowStyle(categoryId);
  return (
    <tr key={`eval-cat-${categoryId}`}>
      <Box
        component="td"
        colSpan={colSpan}
        sx={{
          px: 1.25,
          py: 0.7,
          background: theme.headerBg,
          borderBottom: `2px solid ${theme.accent}`,
          borderTop: withTopBorder ? '2px solid #e2e8f0' : undefined,
        }}
      >
        <Typography
          sx={{
            fontWeight: 900,
            color: theme.headerText,
            letterSpacing: 1.3,
            textTransform: 'uppercase',
            fontSize: '0.8rem',
            fontFamily: SCORECARD_FONT,
          }}
        >
          {theme.label}
        </Typography>
      </Box>
    </tr>
  );
}

const evalCardPaperSx = {
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column' as const,
  m: { xs: 1, md: 1.25 },
  borderRadius: 2.5,
  border: '1px solid #e2e8f0',
  bgcolor: brandColors.white,
  boxShadow:
    '0 14px 36px rgba(17,23,57,0.11), 0 4px 12px rgba(17,23,57,0.07), inset 0 1px 0 rgba(255,255,255,0.75)',
  overflow: 'hidden' as const,
};

function flattenContests(categoryGroups: ContestCategoryGroup[]): ContestCatalogEntry[] {
  return categoryGroups.flatMap((g) => g.contests);
}

const compactGradeChipSx = (selected: boolean, colors: ReturnType<typeof getGradeColor>, wide = false) => ({
  px: wide ? { xs: 0.35, sm: 0.5 } : { xs: 0.15, sm: 0.25 },
  py: wide ? 0.35 : 0.15,
  borderRadius: 0.75,
  bgcolor: selected ? colors.selectedBg : colors.bg,
  border: `1px solid ${selected ? 'rgba(255,255,255,0.35)' : colors.border}`,
  minWidth: wide ? 64 : 0,
  textAlign: 'center' as const,
  fontFamily: SCORECARD_FONT,
});

const scorecardHeaderMetaSx = {
  fontFamily: SCORECARD_FONT,
  fontWeight: 800,
  color: '#475569',
  fontSize: SCORECARD_HEADER_FONT,
  lineHeight: 1.2,
  minWidth: 0,
};

const targetTimeHeaderCellSx = {
  textAlign: 'center' as const,
  fontWeight: 900,
  fontSize: SCORECARD_HEADER_FONT,
  fontFamily: SCORECARD_FONT,
  color: '#475569',
  bgcolor: '#f1f5f9',
  border: '1px solid #cbd5e1',
  borderRadius: 0.75,
  py: 0.15,
  px: { xs: 0.1, sm: 0.2 },
  lineHeight: 1.15,
  minWidth: 0,
  letterSpacing: 0.1,
};

const targetTimeCellSx = (selected = false) => ({
  textAlign: 'center' as const,
  fontWeight: 800,
  fontSize: SCORECARD_VALUE_FONT,
  fontFamily: SCORECARD_FONT,
  color: selected ? '#ffffff' : '#1e293b',
  lineHeight: 1.15,
  px: { xs: 0.1, sm: 0.2 },
  py: 0.15,
  whiteSpace: 'nowrap' as const,
  borderRadius: 0.75,
  bgcolor: selected ? 'rgba(255,255,255,0.16)' : '#f1f5f9',
  border: selected ? '1px solid rgba(255,255,255,0.32)' : '1px solid #cbd5e1',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

const gradeHeaderCellSx = (colors: ReturnType<typeof getGradeColor>) => ({
  textAlign: 'center' as const,
  fontWeight: 900,
  fontSize: SCORECARD_HEADER_FONT,
  fontFamily: SCORECARD_FONT,
  color: colors.text,
  bgcolor: colors.bg,
  border: `1px solid ${colors.border}`,
  borderRadius: 0.75,
  py: 0.15,
  px: { xs: 0.15, sm: 0.25 },
  letterSpacing: 0.2,
  lineHeight: 1.2,
  minWidth: 0,
});

interface CompactContestRowProps {
  index: number;
  contest: ContestCatalogEntry;
  categoryId: ContestCategoryId;
  selected: boolean;
  submitted?: boolean;
  ageCategory: string;
  gradeCodes: string[];
  /** Learn tab — skill name only (no benchmark columns). */
  compactList?: boolean;
  readOnly?: boolean;
  onSelect: () => void;
}

const CompactContestRow: React.FC<CompactContestRowProps> = ({
  index,
  contest,
  categoryId,
  selected,
  submitted = false,
  ageCategory,
  gradeCodes,
  compactList = false,
  readOnly = false,
  onSelect,
}) => {
  const theme = CATEGORY_THEME[categoryId];
  const grades = getGradeBandsForAge(contest, ageCategory);
  const unit = getBenchmarkUnit(contest, ageCategory);
  const gradeByCode = Object.fromEntries(grades.map((g) => [String(g.code), g]));
  const targetTime = getTargetTimeDisplay(contest, ageCategory);
  const benchmarkRow = readOnly && !compactList;
  const rowGrid = compactList
    ? buildLearnListGrid()
    : benchmarkRow
      ? buildBenchmarkScorecardGrid(gradeCodes.length)
      : buildLearnScorecardGrid(gradeCodes.length);

  return (
    <Box
      onClick={readOnly ? undefined : onSelect}
      sx={{
        display: 'grid',
        gridTemplateColumns: rowGrid,
        alignItems: 'center',
        gap: benchmarkRow ? { xs: 0.5, sm: 0.65 } : { xs: 0.35, sm: 0.5 },
        px: { xs: 0.75, sm: 1 },
        py: 0.65,
        minHeight: 44,
        cursor: readOnly ? 'default' : 'pointer',
        borderRadius: 1.25,
        bgcolor: selected ? `${theme.accent}12` : theme.rowBg,
        border: selected ? `2px solid ${theme.accent}` : `1px solid ${theme.rowBorder}`,
        boxShadow: selected ? `inset 8px 0 0 ${theme.accent}` : 'none',
        transition: 'all 0.15s ease',
        justifyItems: benchmarkRow ? 'stretch' : undefined,
        '&:hover': {
          bgcolor: selected ? `${theme.accent}18` : theme.rowHover,
        },
      }}
    >
      <Typography
        sx={{
          fontWeight: 900,
          color: theme.rowTarget,
          textAlign: 'center',
          fontSize: { xs: '0.78rem', sm: '0.85rem' },
        }}
      >
        {index}
      </Typography>
      {!benchmarkRow && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {submitted ? (
            <CheckCircleIcon
              titleAccess="Submitted"
              sx={{ fontSize: 15, color: brandColors.green }}
            />
          ) : (
            <Box sx={{ width: 15, height: 15 }} />
          )}
        </Box>
      )}
      <Typography
        noWrap={false}
        title={contest.title}
        sx={{
          fontWeight: selected ? 800 : 700,
          color: theme.rowText,
          fontSize: { xs: '0.76rem', sm: '0.84rem' },
          lineHeight: 1.2,
          minWidth: 0,
          textAlign: benchmarkRow ? 'left' : undefined,
          wordBreak: 'break-word',
          whiteSpace: 'normal',
        }}
      >
        {contest.title}
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'center', minWidth: 0, px: 0.25 }}>
        <CapabilityLevelChip level={contest.capabilityLevel} compact />
      </Box>
      {!compactList && (
        <>
          <Typography sx={targetTimeCellSx(false)} title={targetTime.title}>
            {targetTime.label}
          </Typography>
          {gradeCodes.map((code) => {
            const band = gradeByCode[code];
            const colors = getGradeColor(code);
            return (
              <Box
                key={code}
                sx={compactGradeChipSx(false, colors, benchmarkRow)}
                title={band ? `Grade ${code}: ${band.label}${unit ? ` ${unit}` : ''}` : `Grade ${code}`}
              >
                <Typography
                  sx={{
                    display: 'block',
                    fontWeight: 800,
                    fontSize: benchmarkRow ? { xs: '0.68rem', sm: '0.74rem' } : SCORECARD_VALUE_FONT,
                    fontFamily: SCORECARD_FONT,
                    lineHeight: 1.2,
                    color: colors.text,
                    whiteSpace: benchmarkRow ? 'normal' : 'nowrap',
                    overflow: benchmarkRow ? 'visible' : 'hidden',
                    textOverflow: benchmarkRow ? 'clip' : 'ellipsis',
                    wordBreak: benchmarkRow ? 'break-word' : undefined,
                  }}
                >
                  {band?.label ?? '—'}
                </Typography>
              </Box>
            );
          })}
        </>
      )}
    </Box>
  );
};

interface ContestScorecardPanelProps {
  categoryGroups: ContestCategoryGroup[];
  selectedContestId: string | null;
  ageCategory: string;
  submittedContestIds?: Set<string> | string[];
  /** Learn tab — contest names only; benchmarks on Benchmark tab and Frame 2. */
  compactList?: boolean;
  /** Benchmark tab — display-only rows. */
  readOnly?: boolean;
  onSelectContest: (contestId: string, categoryId: ContestCategoryId) => void;
}

export const ContestScorecardPanel: React.FC<ContestScorecardPanelProps> = ({
  categoryGroups,
  selectedContestId,
  ageCategory,
  submittedContestIds,
  compactList = false,
  readOnly = false,
  onSelectContest,
}) => {
  const totalCount = categoryGroups.reduce((sum, g) => sum + g.contests.length, 0);
  const hasAnyContest = totalCount > 0;
  const submittedSet =
    submittedContestIds instanceof Set
      ? submittedContestIds
      : new Set(Array.isArray(submittedContestIds) ? submittedContestIds : []);
  let rowNumber = 0;

  const headerGradeCodes = useMemo(
    () => resolveScorecardGradeCodes(flattenContests(categoryGroups), ageCategory),
    [categoryGroups, ageCategory],
  );
  const benchmarkMode = readOnly && !compactList;
  const panelMode = compactList ? 'learn-list' : benchmarkMode ? 'benchmark' : 'learn-scorecard';
  const learnGrid = compactList
    ? buildLearnListGrid()
    : benchmarkMode
      ? buildBenchmarkScorecardGrid(headerGradeCodes.length)
      : buildLearnScorecardGrid(headerGradeCodes.length);
  const panelMinWidth = scorecardGridMinWidth(headerGradeCodes.length, panelMode, 'self_eval');

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        overflowX: benchmarkMode ? 'auto' : 'hidden',
        bgcolor: brandColors.ice,
        '&::-webkit-scrollbar': { width: 6, height: benchmarkMode ? 6 : undefined },
        '&::-webkit-scrollbar-thumb': { bgcolor: '#7dd3fc', borderRadius: 3 },
      }}
    >
      <Box sx={{ minWidth: benchmarkMode ? panelMinWidth : undefined, width: benchmarkMode ? 'fit-content' : '100%' }}>
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          px: { xs: 0.75, sm: 1 },
          py: 0.55,
          bgcolor: '#e2e8f0',
          borderBottom: '1px solid #cbd5e1',
          display: 'grid',
          gridTemplateColumns: learnGrid,
          alignItems: 'center',
          gap: benchmarkMode ? { xs: 0.5, sm: 0.65 } : { xs: 0.35, sm: 0.5 },
        }}
      >
        <Typography variant="caption" sx={{ ...scorecardHeaderMetaSx, textAlign: 'center' }}>
          #
        </Typography>
        {!benchmarkMode && <Box />}
        <Typography
          variant="caption"
          sx={{ ...scorecardHeaderMetaSx, letterSpacing: 0.3, textAlign: benchmarkMode ? 'left' : undefined }}
        >
          Contest
        </Typography>
        <Typography
          variant="caption"
          title="Capability Level"
          sx={{ ...scorecardHeaderMetaSx, textAlign: 'center', lineHeight: 1.15 }}
        >
          Capability
          <Box component="span" sx={{ display: 'block' }}>
            Level
          </Box>
        </Typography>
        {!compactList && (
          <>
            {benchmarkMode ? (
              <Typography variant="caption" title="Target time" sx={benchmarkTargetHeaderCellSx}>
                Target time
              </Typography>
            ) : (
              <Typography variant="caption" title="Target time" sx={targetTimeHeaderCellSx}>
                Target
                <Box component="span" sx={{ display: 'block' }}>
                  time
                </Box>
              </Typography>
            )}
            {headerGradeCodes.map((code) => {
              const colors = getGradeColor(code);
              const tier = getGradeTierLabel(code);
              if (benchmarkMode) {
                return (
                  <Box key={code} title={tier} sx={benchmarkGradeHeaderCellSx(colors, tier)}>
                    <Typography component="span" sx={{ fontWeight: 900, fontSize: SCORECARD_HEADER_FONT }}>
                      {code}
                    </Typography>
                    <Typography
                      component="span"
                      sx={{
                        fontWeight: 700,
                        fontSize: { xs: '0.52rem', sm: '0.58rem' },
                        opacity: 0.9,
                        lineHeight: 1.15,
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        textAlign: 'center',
                      }}
                    >
                      {tier}
                    </Typography>
                  </Box>
                );
              }
              return (
                <Typography key={code} title={tier} sx={gradeHeaderCellSx(colors)}>
                  {code}
                </Typography>
              );
            })}
          </>
        )}
      </Box>

      {!hasAnyContest ? (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: '#64748b' }}>
            No contests published for this type yet.
          </Typography>
        </Box>
      ) : (
        <Stack spacing={1.25} sx={{ p: 1.25 }}>
          {categoryGroups.map((group) => {
            const theme = CATEGORY_THEME[group.id];
            return (
              <Box key={group.id}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 0.6,
                  }}
                >
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 0.6,
                      px: 1.5,
                      py: 0.55,
                      borderRadius: 1.25,
                      background: theme.headerBg,
                      boxShadow: `0 2px 8px ${theme.accent}33`,
                    }}
                  >
                    {React.cloneElement(theme.icon, { sx: { fontSize: 16, color: theme.headerText } })}
                    <Typography
                      sx={{
                        fontWeight: 950,
                        color: theme.headerText,
                        letterSpacing: 1.6,
                        textTransform: 'uppercase',
                        fontSize: '0.9rem',
                        lineHeight: 1,
                        textAlign: 'center',
                      }}
                    >
                      {group.label}
                    </Typography>
                  </Box>
                </Box>

                {group.contests.length === 0 ? (
                  <Typography variant="caption" sx={{ color: '#94a3b8', px: 1, display: 'block', textAlign: 'center' }}>
                    Coming soon
                  </Typography>
                ) : (
                  <Stack spacing={0.5}>
                    {group.contests.map((contest) => {
                      rowNumber += 1;
                      return (
                        <CompactContestRow
                          key={contest.id}
                          index={rowNumber}
                          contest={contest}
                          categoryId={group.id}
                          selected={!readOnly && contest.id === selectedContestId}
                          submitted={submittedSet.has(contest.id)}
                          ageCategory={ageCategory}
                          gradeCodes={headerGradeCodes}
                          compactList={compactList}
                          readOnly={readOnly}
                          onSelect={() => onSelectContest(contest.id, group.id)}
                        />
                      );
                    })}
                  </Stack>
                )}
              </Box>
            );
          })}
        </Stack>
      )}
      </Box>
    </Box>
  );
};

export interface ContestReferenceVideosDialogProps {
  open: boolean;
  contest: ContestCatalogEntry | null;
  onClose: () => void;
}

export const ContestReferenceVideosDialog: React.FC<ContestReferenceVideosDialogProps> = ({
  open,
  contest,
  onClose,
}) => {
  const videos = useMemo(() => (contest ? getOrderedReferenceVideos(contest) : []), [contest]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 1,
          pr: 1,
          fontFamily: '"Sora", Arial, sans-serif',
          fontWeight: 700,
          color: brandColors.navy,
        }}
      >
        <Box>
          <Typography component="span" sx={{ display: 'block', fontWeight: 700, fontSize: '1.05rem' }}>
            Reference videos
          </Typography>
          {contest && (
            <Typography variant="body2" sx={{ color: brandColors.slate, mt: 0.35, fontWeight: 500 }}>
              {contest.title} · {videos.length} clip{videos.length === 1 ? '' : 's'} in order
            </Typography>
          )}
        </Box>
        <IconButton aria-label="Close" onClick={onClose} size="small" sx={{ color: brandColors.slate }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: brandColors.ice, p: { xs: 1.25, md: 1.75 } }}>
        {!contest || videos.length === 0 ? (
          <Box
            sx={{
              py: 4,
              textAlign: 'center',
              borderRadius: 2,
              border: '1px dashed #cbd5e1',
              bgcolor: brandColors.white,
            }}
          >
            <Typography variant="body2" sx={{ color: brandColors.slate }}>
              No reference videos for this skill yet.
            </Typography>
          </Box>
        ) : (
          <Stack spacing={1.5}>
            {videos.map((video) => (
              <ReferenceVideoDialogCard key={video.id} video={video} />
            ))}
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
};

const ReferenceVideoDialogCard: React.FC<{ video: OrderedContestVideo }> = ({ video }) => (
  <Box
    sx={{
      borderRadius: 2,
      overflow: 'hidden',
      border: '1px solid #e2e8f0',
      bgcolor: brandColors.white,
      boxShadow: '0 1px 4px rgba(17,23,57,0.06)',
    }}
  >
    <Box sx={{ position: 'relative' }}>
      <Box
        sx={{
          position: 'absolute',
          top: 8,
          left: 8,
          zIndex: 1,
          minWidth: 26,
          height: 26,
          px: 0.75,
          borderRadius: '999px',
          bgcolor: video.isRelated ? brandColors.violet : brandColors.blue,
          color: brandColors.white,
          fontWeight: 900,
          fontSize: '0.75rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {video.numberLabel}
      </Box>
      <ContestEmbeddedVideo url={video.url} title={video.title} />
    </Box>
    <Box sx={{ px: 1.25, py: 1, borderTop: '1px solid #f1f5f9' }}>
      <Typography sx={{ fontWeight: 800, fontSize: '0.9rem', color: brandColors.navy, lineHeight: 1.35 }}>
        {video.numberLabel} {video.title}
      </Typography>
      {video.isRelated && video.parentTitle && (
        <Typography variant="caption" sx={{ color: brandColors.slate, display: 'block', mt: 0.25 }}>
          Related to: {video.parentTitle}
        </Typography>
      )}
      {video.role && !video.isRelated && (
        <Typography variant="caption" sx={{ color: brandColors.blue, fontWeight: 700, display: 'block', mt: 0.25 }}>
          {video.role === 'primary' ? 'Primary demo' : video.role === 'reference' ? 'Reference' : 'Sample'}
        </Typography>
      )}
      {video.notes && (
        <Typography variant="caption" sx={{ color: brandColors.slate, display: 'block', mt: 0.35 }}>
          {video.notes}
        </Typography>
      )}
    </Box>
  </Box>
);

function ContestSubmittedVideoDialog({
  open,
  title,
  videoUrl,
  selfAttempts,
  selfSuccesses,
  onClose,
}: {
  open: boolean;
  title: string;
  videoUrl: string;
  selfAttempts?: number | string | null;
  selfSuccesses?: number | string | null;
  onClose: () => void;
}) {
  const src = resolveContestVideoSrc(videoUrl);
  const selfCounts =
    selfAttempts !== null && selfAttempts !== undefined && String(selfAttempts).trim() !== ''
      ? `${selfSuccesses ?? '—'} of ${selfAttempts} self-verified`
      : '';
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 800, pr: 6, color: brandColors.navy, fontFamily: SCORECARD_FONT }}>
        Submitted video
        <Typography component="span" sx={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, color: brandColors.slate, mt: 0.35 }}>
          {title}
        </Typography>
        {selfCounts ? (
          <Typography component="span" sx={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, color: brandColors.navy, mt: 0.35 }}>
            {selfCounts}
          </Typography>
        ) : null}
      </DialogTitle>
      <IconButton aria-label="Close" onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
        <CloseIcon />
      </IconButton>
      <DialogContent sx={{ bgcolor: '#0f172a', p: { xs: 0, sm: 0 } }}>
        {src && open ? (
          <Box sx={{ bgcolor: 'black' }}>
            <Box
              component="video"
              controls
              playsInline
              src={src}
              sx={{ width: '100%', maxHeight: { xs: 280, md: 420 }, display: 'block' }}
            >
              Your browser does not support video playback.
            </Box>
          </Box>
        ) : (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography sx={{ color: '#94a3b8' }}>This submitted video is not available to play.</Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 1.5 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', fontWeight: 700 }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ContestSelfNotesDialog({
  open,
  title,
  value,
  readOnly = false,
  extraLabel,
  extraValue,
  onClose,
  onSave,
}: {
  open: boolean;
  title: string;
  value: string;
  readOnly?: boolean;
  extraLabel?: string;
  extraValue?: string;
  onClose: () => void;
  onSave: (notes: string) => void;
}) {
  const [draft, setDraft] = useState(value);

  useEffect(() => {
    if (open) setDraft(value);
  }, [open, value]);

  const extra = String(extraValue || '').trim();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, pr: 5, fontFamily: SCORECARD_FONT }}>{title}</DialogTitle>
      <IconButton aria-label="Close" onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
        <CloseIcon />
      </IconButton>
      <DialogContent>
        <TextField
          multiline
          minRows={5}
          maxRows={14}
          fullWidth
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          disabled={readOnly}
          placeholder={
            readOnly
              ? extraLabel
                ? 'No notes yet.'
                : 'No coach notes yet.'
              : 'Add notes about your performance, conditions, or goals…'
          }
          sx={{ '& .MuiInputBase-root': { fontSize: '0.9rem', lineHeight: 1.5 } }}
        />
        {extra ? (
          <TextField
            multiline
            minRows={3}
            maxRows={8}
            fullWidth
            value={extra}
            disabled
            label={extraLabel || 'Admin feedback'}
            sx={{ mt: 2, '& .MuiInputBase-root': { fontSize: '0.9rem', lineHeight: 1.5 } }}
          />
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          {readOnly ? 'Close' : 'Cancel'}
        </Button>
        {!readOnly && (
          <Button
            variant="contained"
            onClick={() => {
              onSave(draft);
              onClose();
            }}
            sx={{ textTransform: 'none', fontWeight: 700, bgcolor: brandColors.navy }}
          >
            Save notes
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

interface SelfEvalScorecardRowProps {
  index: number;
  contest: ContestCatalogEntry;
  categoryId: ContestCategoryId;
  ageCategory: string;
  genderKey: ContestGenderKey;
  gradeCodes: string[];
  variant: 'self_eval' | 'contest';
  value: string;
  totalAttempts?: string;
  successfulAttempts?: string;
  recordedDate: string;
  submittedDate?: string;
  selfNotes: string;
  coachNotes: string;
  errors: string[];
  grade: ContestGradeCode | null;
  submitted?: boolean;
  submittedVideoUrl?: string | null;
  pendingVideoFile?: File | null;
  uploading?: boolean;
  onValueChange: (value: string) => void;
  onAttemptsChange?: (value: string) => void;
  onSuccessesChange?: (value: string) => void;
  onDateChange: (date: string) => void;
  onSelfNotesChange: (notes: string) => void;
  onErrorsChange?: (errors: string[]) => void;
  onOpenReferenceVideos: () => void;
  onVideoSelected?: (file: File) => void;
  onSubmitVideo?: () => void;
  onBrowseClick?: () => boolean;
  referenceVideoCount: number;
  readOnly?: boolean;
  rowSaved?: boolean;
  onSaveSkill?: () => void;
  onOpenBenchmarks?: () => void;
}

const SelfEvalScorecardRow: React.FC<SelfEvalScorecardRowProps> = ({
  index,
  contest,
  categoryId,
  ageCategory,
  genderKey,
  gradeCodes,
  variant,
  value,
  totalAttempts = '',
  successfulAttempts = '',
  recordedDate,
  submittedDate = '',
  selfNotes,
  coachNotes,
  errors,
  grade,
  submitted = false,
  submittedVideoUrl = null,
  pendingVideoFile = null,
  uploading = false,
  onValueChange,
  onAttemptsChange,
  onSuccessesChange,
  onDateChange,
  onSelfNotesChange,
  onErrorsChange,
  onOpenReferenceVideos,
  onVideoSelected,
  onSubmitVideo,
  onBrowseClick,
  referenceVideoCount,
  readOnly = false,
  rowSaved = false,
  onSaveSkill,
  onOpenBenchmarks,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selfNotesOpen, setSelfNotesOpen] = useState(false);
  const [coachNotesOpen, setCoachNotesOpen] = useState(false);
  const [submittedVideoOpen, setSubmittedVideoOpen] = useState(false);
  const theme = CATEGORY_THEME[categoryId];
  const targetTime = getTargetTimeDisplay(contest, ageCategory, genderKey);

  const inputSx = {
    width: '100%',
    '& .MuiInputBase-root': { bgcolor: brandColors.white, fontSize: '0.85rem' },
    '& .MuiInputBase-input': { py: 0.65, px: 0.75 },
    '& .MuiInputBase-input::placeholder': {
      opacity: variant === 'contest' ? 1 : 0,
      color: '#94a3b8',
    },
  } as const;

  const dateSx = {
    ...inputSx,
    '& input[type="date"]': {
      color: 'inherit',
    },
    '& input[type="date"]::-webkit-calendar-picker-indicator': {
      opacity: 1,
      cursor: readOnly ? 'default' : 'pointer',
    },
  } as const;

  const cellSx = { bgcolor: 'inherit', verticalAlign: 'middle' as const };

  return (
    <Box
      component="tr"
      sx={{
        bgcolor: theme.rowBg,
        transition: 'background-color 0.15s ease',
        '&:hover': { bgcolor: theme.rowHover },
      }}
    >
      <Box component="td" sx={{ ...cellSx, textAlign: 'center', fontWeight: 900, color: theme.rowTarget, width: 36 }}>
        {index}
      </Box>
      <Box component="td" sx={{ ...cellSx, textAlign: 'left', minWidth: EVAL_CONTEST_COL_MIN, maxWidth: 220 }} title={contest.title}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.4 }}>
          <Typography
            sx={{
              fontWeight: 700,
              color: theme.rowText,
              fontSize: '0.875rem',
              lineHeight: 1.35,
              wordBreak: 'break-word',
              whiteSpace: 'normal',
              flex: 1,
              minWidth: 0,
            }}
          >
            {contest.title}
          </Typography>
          {onOpenBenchmarks || onOpenReferenceVideos ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.35, flexShrink: 0, mt: -0.25 }}>
              {onOpenBenchmarks ? (
                <Tooltip title="Benchmarks for this skill">
                  <IconButton
                    size="small"
                    aria-label={`Benchmarks for ${contest.title}`}
                    onClick={onOpenBenchmarks}
                    sx={{
                      color: brandColors.violet,
                      bgcolor: '#efe6f8',
                      border: `1px solid ${brandColors.violet}55`,
                      width: 26,
                      height: 26,
                      '&:hover': { bgcolor: '#e2d0f4', borderColor: brandColors.violet },
                    }}
                  >
                    <MatchIntelIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>
              ) : null}
              <Tooltip title={referenceVideoCount ? `Reference videos (${referenceVideoCount})` : 'Reference videos'}>
                <IconButton
                  size="small"
                  aria-label={`Reference videos for ${contest.title}`}
                  onClick={onOpenReferenceVideos}
                  sx={{
                    color: brandColors.blue,
                    bgcolor: brandColors.white,
                    border: `1px solid ${brandColors.blue}55`,
                    width: 26,
                    height: 26,
                    '&:hover': { bgcolor: '#e8eefc', borderColor: brandColors.blue },
                  }}
                >
                  <SmartDisplayIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </Box>
          ) : null}
        </Box>
      </Box>
      <Box component="td" sx={{ ...cellSx, textAlign: 'center', width: 92, px: 0.4 }}>
        <CapabilityLevelChip level={contest.capabilityLevel} compact />
      </Box>
      <Box component="td" sx={{ ...cellSx, textAlign: 'center', fontWeight: 600, color: theme.rowText, whiteSpace: 'nowrap', width: 64 }} title={targetTime.title}>
        {targetTime.label}
      </Box>
      <Box component="td" sx={{ ...cellSx, textAlign: 'center' }}>
        {variant === 'contest' ? (
          <Typography
            variant="caption"
            sx={{ fontWeight: value.trim() ? 800 : 600, color: value.trim() ? brandColors.navy : brandColors.slate }}
            title={
              submitted && !value.trim()
                ? 'Admin will enter the contest score after validating this video'
                : undefined
            }
          >
            {value.trim() ? value : submitted ? 'Pending' : '—'}
          </Typography>
        ) : (
          <TextField
            size="small"
            type="number"
            value={value}
            onChange={(e) => onValueChange(e.target.value)}
            inputProps={{ step: 'any' }}
            disabled={readOnly}
            sx={inputSx}
          />
        )}
      </Box>
      <Box component="td" sx={{ ...cellSx, textAlign: 'center' }}>
        {!value.trim() ? (
          <Typography
            variant="caption"
            color="text.secondary"
            title={
              variant === 'contest' && submitted
                ? 'Grade appears after admin validates the video and enters a score'
                : undefined
            }
          >
            {variant === 'contest' && submitted ? 'Pending' : '—'}
          </Typography>
        ) : grade ? (
          <Chip
            size="small"
            label={grade}
            sx={{
              fontWeight: 900,
              height: 22,
              minWidth: 28,
              bgcolor: getGradeColor(grade).bg,
              color: getGradeColor(grade).text,
              border: `1px solid ${getGradeColor(grade).border}`,
            }}
          />
        ) : (
          <Chip
            size="small"
            label="—"
            title="Grade ranges are not published yet. The grade will update when benchmarks are added or revised."
            sx={{ fontWeight: 800, height: 22 }}
          />
        )}
      </Box>
      {variant === 'contest' && (
        <>
          <Box component="td" sx={{ ...cellSx, textAlign: 'center', width: 56, px: 0.3 }}>
            {submitted || readOnly ? (
              <Typography variant="caption" sx={{ fontWeight: 800, color: totalAttempts.trim() ? brandColors.navy : brandColors.slate }}>
                {totalAttempts.trim() || '—'}
              </Typography>
            ) : (
              <TextField
                size="small"
                type="number"
                value={totalAttempts}
                onChange={(e) => onAttemptsChange?.(e.target.value)}
                inputProps={{ min: 0, step: 1, inputMode: 'numeric' }}
                placeholder="0"
                title="Total attempts for this skill"
                sx={inputSx}
              />
            )}
          </Box>
          <Box component="td" sx={{ ...cellSx, textAlign: 'center', width: 56, px: 0.3 }}>
            {submitted || readOnly ? (
              <Typography variant="caption" sx={{ fontWeight: 800, color: successfulAttempts.trim() ? brandColors.navy : brandColors.slate }}>
                {successfulAttempts.trim() || '—'}
              </Typography>
            ) : (
              <TextField
                size="small"
                type="number"
                value={successfulAttempts}
                onChange={(e) => onSuccessesChange?.(e.target.value)}
                inputProps={{ min: 0, step: 1, inputMode: 'numeric' }}
                placeholder="0"
                title="Successful attempts — cannot exceed total attempts"
                error={
                  Boolean(totalAttempts.trim()) &&
                  Boolean(successfulAttempts.trim()) &&
                  Number(successfulAttempts) > Number(totalAttempts)
                }
                sx={inputSx}
              />
            )}
          </Box>
        </>
      )}
      <Box component="td" sx={cellSx}>
        <TextField
          size="small"
          type="date"
          value={recordedDate}
          onChange={(e) => onDateChange(e.target.value)}
          disabled={readOnly || (variant === 'contest' && submitted)}
          required={!readOnly && (variant === 'self_eval' || (variant === 'contest' && !submitted))}
          InputLabelProps={{ shrink: true }}
          title={
            variant === 'contest'
              ? submitted
                ? 'Date this video was recorded'
                : 'Required — date you recorded this video'
              : undefined
          }
          sx={dateSx}
        />
      </Box>
      {variant === 'contest' && (
        <Box component="td" sx={{ ...cellSx, textAlign: 'center' }}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: submittedDate.trim() ? 700 : 600,
              color: submittedDate.trim() ? brandColors.navy : brandColors.slate,
              whiteSpace: 'nowrap',
              display: 'block',
              lineHeight: 1.25,
            }}
          >
            {submittedDate.trim() || '—'}
          </Typography>
        </Box>
      )}
      <Box component="td" sx={{ ...cellSx, textAlign: 'center', width: 44 }}>
        <IconButton
          size="small"
          aria-label={
            variant === 'contest'
              ? selfNotes.trim() || coachNotes.trim()
                ? 'Edit notes'
                : 'Add notes'
              : selfNotes.trim()
                ? 'Edit self notes'
                : 'Add self notes'
          }
          onClick={() => setSelfNotesOpen(true)}
          sx={{
            color: selfNotes.trim() || (variant === 'contest' && coachNotes.trim()) ? brandColors.blue : '#94a3b8',
            bgcolor: selfNotes.trim() || (variant === 'contest' && coachNotes.trim()) ? '#e8eefc' : brandColors.white,
            border: `1px solid ${
              selfNotes.trim() || (variant === 'contest' && coachNotes.trim()) ? brandColors.blue : '#cbd5e1'
            }`,
            '&:hover': { bgcolor: '#e8eefc', borderColor: brandColors.blue },
          }}
        >
          <NotesIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>
      {variant === 'self_eval' && (
        <Box component="td" sx={cellSx}>
          {(() => {
            const options = (contest.commonMistakes || []).map((item) => String(item || '').trim()).filter(Boolean);
            const selected = errors.filter((item) => options.includes(item));
            if (!options.length) {
              return (
                <Typography variant="caption" color="text.secondary" title="No common mistakes defined for this skill">
                  —
                </Typography>
              );
            }
            return (
              <Tooltip title={selected.length ? selected.join(', ') : 'Select one or more common mistakes'}>
                <FormControl size="small" fullWidth>
                  <Select
                    multiple
                    displayEmpty
                    value={selected}
                    disabled={readOnly}
                    onChange={(e) => onErrorsChange?.(e.target.value as string[])}
                    renderValue={(value) => {
                      const list = value as string[];
                      if (!list.length) return '—';
                      if (list.length === 1) return list[0];
                      return `${list.length} selected`;
                    }}
                    sx={{
                      bgcolor: brandColors.white,
                      fontSize: '0.75rem',
                      '& .MuiSelect-select': { py: 0.65, px: 0.75 },
                    }}
                  >
                    {options.map((option) => (
                      <MenuItem key={option} value={option} sx={{ py: 0.5 }}>
                        <Checkbox size="small" checked={selected.includes(option)} sx={{ py: 0, pl: 0 }} />
                        <Typography variant="caption">{option}</Typography>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Tooltip>
            );
          })()}
        </Box>
      )}
      {variant === 'contest' ? (
        <Box component="td" sx={{ ...cellSx, textAlign: 'center' }}>
          {submitted ? (
            <Stack spacing={0.5} alignItems="center">
              <Chip
                size="small"
                icon={<CheckCircleIcon sx={{ fontSize: '16px !important' }} />}
                label="Submitted"
                sx={{ fontWeight: 700, height: 26, bgcolor: '#ECF8E8', color: '#2F7A1F', border: '1px solid #A8D99A' }}
              />
              <Button
                size="small"
                variant="outlined"
                startIcon={<PlayCircleOutlineIcon sx={{ fontSize: '16px !important' }} />}
                disabled={!submittedVideoUrl}
                onClick={() => setSubmittedVideoOpen(true)}
                aria-label={`View submitted video for ${contest.title}`}
                title={submittedVideoUrl ? 'Watch the submitted contest video' : 'Video file is not available'}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: '0.72rem',
                  px: 1,
                  minWidth: 0,
                  color: brandColors.navy,
                  borderColor: `${brandColors.navy}55`,
                  '&:hover': { borderColor: brandColors.navy, bgcolor: '#eef2f8' },
                  '&.Mui-disabled': { color: '#94a3b8', borderColor: '#cbd5e1' },
                }}
              >
                View
              </Button>
            </Stack>
          ) : readOnly ? (
            <Typography variant="caption" color="text.secondary">
              —
            </Typography>
          ) : uploading ? (
            <CircularProgress size={22} sx={{ color: brandColors.orange }} />
          ) : (
            <Stack spacing={0.5} alignItems="center">
              <Button
                size="small"
                variant="outlined"
                disabled={readOnly}
                onClick={() => {
                  if (readOnly) return;
                  if (onBrowseClick && onBrowseClick() === false) return;
                  fileInputRef.current?.click();
                }}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  px: 1,
                  minWidth: 0,
                  color: brandColors.navy,
                  borderColor: `${brandColors.navy}55`,
                  '&:hover': { borderColor: brandColors.navy, bgcolor: '#eef2f8' },
                }}
              >
                Browse
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={onSubmitVideo}
                disabled={
                  readOnly ||
                  !pendingVideoFile ||
                  !recordedDate.trim() ||
                  !/^\d+$/.test(totalAttempts.trim()) ||
                  !/^\d+$/.test(successfulAttempts.trim()) ||
                  Number(successfulAttempts) > Number(totalAttempts)
                }
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  px: 1.25,
                  minWidth: 0,
                  bgcolor: brandColors.orange,
                  '&:hover': { bgcolor: '#c94909' },
                  '&.Mui-disabled': { bgcolor: '#cbd5e1', color: '#64748b' },
                }}
              >
                Submit
              </Button>
              {pendingVideoFile ? (
                <Typography variant="caption" noWrap title={pendingVideoFile.name} sx={{ maxWidth: 112, color: brandColors.slate, fontSize: '0.65rem' }}>
                  {pendingVideoFile.name}
                </Typography>
              ) : null}
              <input
                ref={fileInputRef}
                hidden
                type="file"
                accept="video/*"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) onVideoSelected?.(file);
                  e.target.value = '';
                }}
              />
            </Stack>
          )}
        </Box>
      ) : (
        <Box component="td" sx={{ ...cellSx, textAlign: 'center', width: 44 }}>
          <IconButton
            size="small"
            aria-label={coachNotes.trim() ? 'View coach notes' : 'Coach notes'}
            onClick={() => setCoachNotesOpen(true)}
            disabled={!coachNotes.trim()}
            sx={{
              color: coachNotes.trim() ? brandColors.navy : '#94a3b8',
              bgcolor: coachNotes.trim() ? '#eef2f8' : brandColors.white,
              border: `1px solid ${coachNotes.trim() ? brandColors.navy : '#cbd5e1'}55`,
            }}
          >
            <AssignmentIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      )}
      {variant === 'self_eval' && (
        <Box component="td" sx={{ ...cellSx, textAlign: 'center', width: 64 }}>
          {readOnly ? (
            rowSaved ? (
              <CheckCircleIcon sx={{ fontSize: 18, color: '#2F7A1F' }} />
            ) : (
              <Typography variant="caption" color="text.secondary">
                —
              </Typography>
            )
          ) : rowSaved ? (
            <Chip
              size="small"
              icon={<CheckCircleIcon sx={{ fontSize: '14px !important' }} />}
              label="Saved"
              sx={{ fontWeight: 700, height: 24, bgcolor: '#ECF8E8', color: '#2F7A1F', border: '1px solid #A8D99A' }}
            />
          ) : (
            <Button
              size="small"
              variant="contained"
              startIcon={<SaveIcon sx={{ fontSize: '16px !important' }} />}
              onClick={onSaveSkill}
              disabled={!value.trim() || !recordedDate.trim() || !onSaveSkill}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.72rem',
                px: 1,
                minWidth: 0,
                bgcolor: brandColors.blue,
                '&:hover': { bgcolor: '#3a56c9' },
                '&.Mui-disabled': { bgcolor: '#cbd5e1', color: '#64748b' },
              }}
            >
              Save
            </Button>
          )}
        </Box>
      )}
      <ContestSelfNotesDialog
        open={selfNotesOpen}
        title={`Notes — ${contest.title}`}
        value={selfNotes}
        readOnly={readOnly}
        extraLabel={variant === 'contest' ? 'Admin feedback' : undefined}
        extraValue={variant === 'contest' ? coachNotes : undefined}
        onClose={() => setSelfNotesOpen(false)}
        onSave={onSelfNotesChange}
      />
      {variant === 'contest' && (
        <ContestSubmittedVideoDialog
          open={submittedVideoOpen}
          title={contest.title}
          videoUrl={submittedVideoUrl || ''}
          selfAttempts={totalAttempts}
          selfSuccesses={successfulAttempts}
          onClose={() => setSubmittedVideoOpen(false)}
        />
      )}
      {variant === 'self_eval' && (
        <ContestSelfNotesDialog
          open={coachNotesOpen}
          title={`Coach notes — ${contest.title}`}
          value={coachNotes}
          readOnly
          onClose={() => setCoachNotesOpen(false)}
          onSave={() => undefined}
        />
      )}
    </Box>
  );
};

export interface ContestSelfEvalScorecardPanelProps {
  variant?: 'self_eval' | 'contest';
  categoryGroups: ContestCategoryGroup[];
  ageCategory: string;
  genderKey?: ContestGenderKey;
  entries: Record<
    string,
    {
      value?: string;
      totalAttempts?: string;
      successfulAttempts?: string;
      recordedDate?: string;
      submittedDate?: string;
      selfNotes?: string;
      coachNotes?: string;
      errors?: string[];
      grade?: ContestGradeCode | null;
      savedAt?: string;
    }
  >;
  submittedContestIds?: Set<string> | string[];
  pendingVideoFiles?: Record<string, File | undefined>;
  submittedVideoUrls?: Record<string, string | undefined>;
  uploadingContestId?: string | null;
  onValueChange: (contestId: string, value: string) => void;
  onAttemptsChange?: (contestId: string, value: string) => void;
  onSuccessesChange?: (contestId: string, value: string) => void;
  onDateChange: (contestId: string, date: string) => void;
  onSelfNotesChange: (contestId: string, notes: string) => void;
  onErrorsChange?: (contestId: string, errors: string[]) => void;
  onVideoSelected?: (contestId: string, file: File) => void;
  onSubmitVideo?: (contestId: string) => void;
  onBrowseClick?: (contestId: string) => boolean;
  onSaveSkill?: (contestId: string) => void;
  readOnly?: boolean;
}

export const ContestSelfEvalScorecardPanel: React.FC<ContestSelfEvalScorecardPanelProps> = ({
  variant = 'self_eval',
  categoryGroups,
  ageCategory,
  genderKey = 'boys',
  entries,
  submittedContestIds,
  pendingVideoFiles,
  submittedVideoUrls,
  uploadingContestId = null,
  onValueChange,
  onAttemptsChange,
  onSuccessesChange,
  onDateChange,
  onSelfNotesChange,
  onErrorsChange,
  onVideoSelected,
  onSubmitVideo,
  onBrowseClick,
  onSaveSkill,
  readOnly = false,
}) => {
  const [videoDialogContest, setVideoDialogContest] = useState<ContestCatalogEntry | null>(null);
  const [benchmarkContest, setBenchmarkContest] = useState<ContestCatalogEntry | null>(null);
  const submittedSet =
    submittedContestIds instanceof Set
      ? submittedContestIds
      : new Set(Array.isArray(submittedContestIds) ? submittedContestIds : []);
  const headerGradeCodes = useMemo(
    () => resolveScorecardGradeCodes(flattenContests(categoryGroups), ageCategory, genderKey),
    [categoryGroups, ageCategory, genderKey],
  );
  const minWidth = scorecardGridMinWidth(headerGradeCodes.length, 'eval', variant);
  const totalCount = categoryGroups.reduce((sum, g) => sum + g.contests.length, 0);
  const hasAnyContest = totalCount > 0;
  const hideBenchmarkReference = true;
  const evalColSpan = 4 + (hideBenchmarkReference ? 0 : headerGradeCodes.length) + (variant === 'self_eval' ? 7 : 8);
  const entryZoneLabel = variant === 'contest' ? 'Your submission' : 'Your self evaluation';
  let rowNumber = 0;

  return (
    <>
      <Paper elevation={0} sx={evalCardPaperSx}>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
            bgcolor: brandColors.ice,
            '&::-webkit-scrollbar': { width: 6, height: 6 },
            '&::-webkit-scrollbar-thumb': { bgcolor: '#7dd3fc', borderRadius: 3 },
          }}
        >
          {!hasAnyContest ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: '#64748b' }}>
                No contests published for this type yet.
              </Typography>
            </Box>
          ) : (
            <Box component="table" sx={{ ...evalCardTableSx, minWidth }}>
              <colgroup>
                <col style={{ width: 36 }} />
                <col style={{ minWidth: EVAL_CONTEST_COL_MIN, width: '20%' }} />
                <col style={{ width: 92 }} />
                <col style={{ width: 64 }} />
                {hideBenchmarkReference
                  ? null
                  : headerGradeCodes.map((code) => (
                      <col key={code} style={{ width: EVAL_REF_GRADE_COL_PX }} />
                    ))}
                <col style={{ width: 72 }} />
                <col style={{ width: 48 }} />
                {variant === 'contest' ? <col style={{ width: 56 }} /> : null}
                {variant === 'contest' ? <col style={{ width: 56 }} /> : null}
                <col style={{ width: 108 }} />
                {variant === 'contest' ? <col style={{ width: 108 }} /> : null}
                <col style={{ width: 40 }} />
                {variant === 'self_eval' ? <col style={{ width: 160 }} /> : null}
                <col style={{ width: variant === 'contest' ? 128 : 40 }} />
                {variant === 'self_eval' ? <col style={{ width: 72 }} /> : null}
              </colgroup>
              <thead>
                <tr>
                  <Box component="th" rowSpan={2} sx={{ width: 36 }}>
                    #
                  </Box>
                  <Box component="th" rowSpan={2} sx={{ textAlign: 'left !important', minWidth: EVAL_CONTEST_COL_MIN }}>
                    Contest
                  </Box>
                  <Box
                    component="th"
                    rowSpan={2}
                    title="Capability Level"
                    sx={{ width: 92, fontSize: '0.72rem', lineHeight: 1.15, px: 0.4 }}
                  >
                    Capability
                    <Box component="span" sx={{ display: 'block', fontWeight: 600 }}>
                      Level
                    </Box>
                  </Box>
                  <Box component="th" rowSpan={2} sx={{ width: 64 }}>
                    Target
                  </Box>
                  {hideBenchmarkReference ? null : (
                    <Box
                      component="th"
                      colSpan={headerGradeCodes.length}
                      sx={{
                        bgcolor: `${EVAL_REF_ZONE_HEADER} !important`,
                        color: '#475569 !important',
                        fontSize: '0.68rem',
                        fontWeight: 800,
                        letterSpacing: 0.6,
                        textTransform: 'uppercase',
                        py: 0.5,
                        borderBottom: '1px solid #94a3b8',
                      }}
                    >
                      Benchmark reference
                    </Box>
                  )}
                  <Box
                    component="th"
                    colSpan={variant === 'self_eval' ? 7 : 8}
                    sx={{
                      bgcolor: `${EVAL_ENTRY_ZONE_BG} !important`,
                      color: brandColors.navy,
                      fontSize: '0.68rem',
                      fontWeight: 800,
                      letterSpacing: 0.6,
                      textTransform: 'uppercase',
                      py: 0.5,
                      borderBottom: `1px solid ${brandColors.blue}44`,
                    }}
                  >
                    {entryZoneLabel}
                  </Box>
                </tr>
                <tr>
                  {hideBenchmarkReference
                    ? null
                    : headerGradeCodes.map((code) => {
                        const tier = getGradeTierLabel(code);
                        return (
                          <Box component="th" key={code} title={tier} sx={evalRefGradeHeaderSx}>
                            {code}
                          </Box>
                        );
                      })}
                  <Box
                    component="th"
                    title={variant === 'contest' ? 'Set by admin after video validation' : undefined}
                    sx={{ bgcolor: `${EVAL_ENTRY_ZONE_BG} !important`, fontSize: '0.75rem' }}
                  >
                    Score
                  </Box>
                  <Box component="th" sx={{ bgcolor: `${EVAL_ENTRY_ZONE_BG} !important`, fontSize: '0.75rem' }}>
                    Grade
                  </Box>
                  {variant === 'contest' && (
                    <>
                      <Box
                        component="th"
                        title="Player self-verified total attempts"
                        sx={{ bgcolor: `${EVAL_ENTRY_ZONE_BG} !important`, fontSize: '0.68rem', lineHeight: 1.15, px: 0.3 }}
                      >
                        Attempts
                      </Box>
                      <Box
                        component="th"
                        title="Player self-verified successful attempts"
                        sx={{ bgcolor: `${EVAL_ENTRY_ZONE_BG} !important`, fontSize: '0.68rem', lineHeight: 1.15, px: 0.3 }}
                      >
                        Success
                      </Box>
                    </>
                  )}
                  <Box component="th" sx={{ bgcolor: `${EVAL_ENTRY_ZONE_BG} !important`, fontSize: '0.75rem' }}>
                    {variant === 'contest' ? 'Recorded' : 'Date'}
                  </Box>
                  {variant === 'contest' && (
                    <Box
                      component="th"
                      sx={{ bgcolor: `${EVAL_ENTRY_ZONE_BG} !important`, fontSize: '0.75rem' }}
                    >
                      Submitted
                    </Box>
                  )}
                  <Box component="th" sx={{ bgcolor: `${EVAL_ENTRY_ZONE_BG} !important`, fontSize: '0.75rem', px: 0.25 }}>
                    Notes
                  </Box>
                  {variant === 'self_eval' && (
                    <Box component="th" sx={{ bgcolor: `${EVAL_ENTRY_ZONE_BG} !important`, fontSize: '0.75rem', px: 0.25 }}>
                      Error
                    </Box>
                  )}
                  <Box component="th" sx={{ bgcolor: `${EVAL_ENTRY_ZONE_BG} !important`, fontSize: '0.75rem', px: 0.25 }}>
                    {variant === 'contest' ? 'Video' : 'Coach'}
                  </Box>
                  {variant === 'self_eval' && (
                    <Box component="th" sx={{ bgcolor: `${EVAL_ENTRY_ZONE_BG} !important`, fontSize: '0.75rem', px: 0.25 }}>
                      Save
                    </Box>
                  )}
                </tr>
              </thead>
              <tbody>
                {categoryGroups.map((group, groupIndex) => {
                  if (!group.contests.length) return null;
                  return (
                    <React.Fragment key={group.id}>
                      {evalCategoryHeaderRow(evalColSpan, group.id, groupIndex > 0)}
                      {group.contests.map((contest) => {
                        rowNumber += 1;
                        const entry = entries[contest.id];
                        const videoCount = getOrderedReferenceVideos(contest).length;
                        return (
                          <SelfEvalScorecardRow
                            key={`${group.id}-${contest.id}`}
                            index={rowNumber}
                            contest={contest}
                            categoryId={group.id}
                            ageCategory={ageCategory}
                            genderKey={genderKey}
                            gradeCodes={headerGradeCodes}
                            variant={variant}
                            value={entry?.value ?? ''}
                            totalAttempts={entry?.totalAttempts ?? ''}
                            successfulAttempts={entry?.successfulAttempts ?? ''}
                            recordedDate={entry?.recordedDate ?? ''}
                            submittedDate={entry?.submittedDate ?? ''}
                            selfNotes={entry?.selfNotes ?? ''}
                            coachNotes={entry?.coachNotes ?? ''}
                            errors={entry?.errors ?? []}
                            grade={entry?.grade ?? null}
                            submitted={submittedSet.has(contest.id)}
                            submittedVideoUrl={submittedVideoUrls?.[contest.id] || null}
                            pendingVideoFile={pendingVideoFiles?.[contest.id] ?? null}
                            uploading={uploadingContestId === contest.id}
                            referenceVideoCount={videoCount}
                            onValueChange={(v) => onValueChange(contest.id, v)}
                            onAttemptsChange={onAttemptsChange ? (v) => onAttemptsChange(contest.id, v) : undefined}
                            onSuccessesChange={onSuccessesChange ? (v) => onSuccessesChange(contest.id, v) : undefined}
                            onDateChange={(d) => onDateChange(contest.id, d)}
                            onSelfNotesChange={(n) => onSelfNotesChange(contest.id, n)}
                            onErrorsChange={onErrorsChange ? (selected) => onErrorsChange(contest.id, selected) : undefined}
                            onOpenReferenceVideos={() => setVideoDialogContest(contest)}
                            onVideoSelected={onVideoSelected ? (file) => onVideoSelected(contest.id, file) : undefined}
                            onSubmitVideo={onSubmitVideo ? () => onSubmitVideo(contest.id) : undefined}
                            onBrowseClick={onBrowseClick ? () => onBrowseClick(contest.id) : undefined}
                            rowSaved={Boolean(entry?.savedAt)}
                            onSaveSkill={onSaveSkill ? () => onSaveSkill(contest.id) : undefined}
                            onOpenBenchmarks={() => setBenchmarkContest(contest)}
                            readOnly={readOnly}
                          />
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </Box>
          )}
        </Box>
      </Paper>
      <ContestReferenceVideosDialog
        open={Boolean(videoDialogContest)}
        contest={videoDialogContest}
        onClose={() => setVideoDialogContest(null)}
      />
      <Dialog
        open={Boolean(benchmarkContest)}
        onClose={() => setBenchmarkContest(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pr: 6, fontWeight: 800, color: brandColors.navy }}>
          {benchmarkContest?.title}
          <Typography component="span" sx={{ display: 'block', fontSize: '0.78rem', fontWeight: 600, color: brandColors.slate, mt: 0.35 }}>
            Benchmarks · {ageCategory}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <ContestSkillBenchmarkBlock contest={benchmarkContest} ageCategory={ageCategory} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBenchmarkContest(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

interface ContestDetailAccordionProps {
  contest: ContestCatalogEntry | null;
  ageCategory: string;
  activeSectionKey: ContestSectionKey;
  onSectionChange: (key: ContestSectionKey) => void;
  onSubmit: () => void;
  /** Opens contest self-eval form (separate from Training/Ranking) */
  onSelfEvaluate: () => void;
  /** Entry already submitted for the selected contest */
  entrySubmitted?: boolean;
  /** Show video upload panel below action buttons */
  showUploadPanel?: boolean;
  uploadPanel?: React.ReactNode;
  /** Learn tab — hide submit / self-eval actions */
  browseOnly?: boolean;
}

const ContestActionBar: React.FC<{
  onSubmit: () => void;
  onSelfEvaluate: () => void;
  entrySubmitted?: boolean;
}> = ({ onSubmit, onSelfEvaluate, entrySubmitted = false }) => (
  <Stack
    direction={{ xs: 'column', sm: 'row' }}
    spacing={1.25}
    justifyContent="stretch"
    sx={{
      py: 1.5,
      px: 1.5,
      bgcolor: brandColors.white,
      borderBottom: '1px solid #e2e8f0',
      gap: 1.25,
    }}
  >
    <GlossyActionButton
      label="Self evaluation"
      variant="navy"
      fullWidth
      startIcon={<AssessmentIcon sx={{ fontSize: 20 }} />}
      onClick={onSelfEvaluate}
    />
    <GlossyActionButton
      label="Submit entry"
      pressedLabel="Submitted"
      variant="orange"
      fullWidth
      pressed={entrySubmitted}
      startIcon={
        entrySubmitted ? (
          <CheckCircleIcon sx={{ fontSize: 20 }} />
        ) : (
          <UploadIcon sx={{ fontSize: 20 }} />
        )
      }
      onClick={onSubmit}
    />
  </Stack>
);

const SelectedSkillBanner: React.FC<{ title: string; subtitle?: string }> = ({ title, subtitle }) => (
  <Box
    sx={{
      px: 1.5,
      py: 1.15,
      bgcolor: brandColors.white,
      borderBottom: '1px solid #e2e8f0',
    }}
  >
    <Typography
      sx={{
        fontWeight: 700,
        color: brandColors.navy,
        fontSize: { xs: '0.95rem', md: '1.1rem' },
        lineHeight: 1.3,
        fontFamily: '"Sora", Arial, sans-serif',
      }}
    >
      {title}
    </Typography>
    {subtitle && (
      <Typography sx={{ color: brandColors.slate, fontSize: { xs: '0.88rem', md: '0.92rem' }, mt: 0.35, lineHeight: 1.4 }}>
        {subtitle}
      </Typography>
    )}
  </Box>
);

export const ContestDetailAccordion: React.FC<ContestDetailAccordionProps> = ({
  contest,
  ageCategory,
  activeSectionKey,
  onSectionChange,
  onSubmit,
  onSelfEvaluate,
  entrySubmitted = false,
  showUploadPanel = false,
  uploadPanel,
  browseOnly = false,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [openSectionKeys, setOpenSectionKeys] = useState<ContestSectionKey[]>([activeSectionKey]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
    setOpenSectionKeys([activeSectionKey]);
  }, [contest?.id]);

  const toggleSection = (key: ContestSectionKey, isExpanded: boolean) => {
    setOpenSectionKeys((prev) => {
      if (isExpanded) {
        return prev.includes(key) ? prev : [...prev, key];
      }
      return prev.filter((item) => item !== key);
    });
    if (isExpanded) onSectionChange(key);
  };

  if (!contest) {
    return (
      <Box sx={{ p: 3, flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: brandColors.ice }}>
        <Typography variant="body2" sx={{ color: '#64748b', textAlign: 'center' }}>
          Select a contest card to view scoring, preparation, and safety details.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      ref={scrollRef}
      sx={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column', bgcolor: brandColors.ice }}
    >
      <SelectedSkillBanner title={contest.title} subtitle={contest.subtitle} />

      <Box sx={{ px: 1.5, py: 1.25, bgcolor: brandColors.ice, borderBottom: '1px solid #e2e8f0' }}>
        <ContestSkillBenchmarkBlock contest={contest} ageCategory={ageCategory} />
      </Box>

      {!browseOnly && (
        <ContestActionBar
          onSubmit={onSubmit}
          onSelfEvaluate={onSelfEvaluate}
          entrySubmitted={entrySubmitted}
        />
      )}

      {!browseOnly && showUploadPanel && uploadPanel}

      {getVisibleFrame2Sections(contest).map((sectionMeta) => {
        const resolved = getSectionContentForAge(contest, sectionMeta.key, ageCategory) || {
          label: sectionMeta.label,
          content: '',
          bullets: [] as string[],
          blocks: [],
        };
        const expanded = openSectionKeys.includes(sectionMeta.key);
        const isAgeSpecific = Boolean(getSectionByKey(contest, sectionMeta.key)?.byAge);
        const sectionNumber = formatContestVideoNumber(sectionMeta.visibleIndex);

        return (
          <Accordion
            key={sectionMeta.key}
            expanded={expanded}
            onChange={(_, isExpanded) => toggleSection(sectionMeta.key, isExpanded)}
            disableGutters
            elevation={0}
            sx={{
              bgcolor: '#fff',
              '&:before': { display: 'none' },
              borderTop: '1px solid #e2e8f0',
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ color: expanded ? brandColors.blue : '#94a3b8' }} />}
              sx={{
                bgcolor: expanded ? brandColors.ice : brandColors.white,
                minHeight: 48,
                '& .MuiAccordionSummary-content': { my: 0.75 },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1, pr: 0.5 }}>
                <Typography
                  sx={{
                    flex: 1,
                    fontWeight: 700,
                    color: expanded ? brandColors.navy : brandColors.slate,
                    fontSize: '0.88rem',
                  }}
                >
                  {sectionNumber} {resolved.label}
                </Typography>
                {isAgeSpecific ? <ContestAgeBadge age={ageCategory} /> : null}
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ bgcolor: '#fafafa', pt: 0 }}>
              {resolved.blocks?.length ? (
                <ContestSectionBlocksView blocks={resolved.blocks} sectionIndex={sectionMeta.visibleIndex} />
              ) : (
                <>
                  <Typography variant="body2" sx={{ color: '#475569', mb: 1, fontSize: '0.85rem', lineHeight: 1.55 }}>
                    {resolved.content}
                  </Typography>
                  {resolved.bullets.length > 0 && (
                    <Stack component="ul" spacing={0.4} sx={{ m: 0, pl: 2 }}>
                      {resolved.bullets.map((bullet) => (
                        <Typography key={bullet} component="li" variant="body2" sx={{ color: '#64748b', fontSize: '0.82rem' }}>
                          {bullet}
                        </Typography>
                      ))}
                    </Stack>
                  )}
                </>
              )}
              {sectionMeta.key === 'motor_psychomotor' && (
                <Button
                  component={RouterLink}
                  to="/motor-psychomotor"
                  size="small"
                  sx={{ mt: 1.5, textTransform: 'none', fontWeight: 800, color: brandColors.orange }}
                >
                  Open Motor & Psychomotor
                </Button>
              )}
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
};

interface ContestVideoPanelProps {
  contest: ContestCatalogEntry | null;
  activeSectionKey: ContestSectionKey;
  onSelectVideo: (videoId: string) => void;
  activeVideoId: string | null;
  /** Journey age — Frame 3 shows this age only (multi-age compare is a separate popup). */
  ageCategory?: string;
}

export const ContestVideoPanel: React.FC<ContestVideoPanelProps> = ({
  contest,
  activeSectionKey,
  onSelectVideo,
  activeVideoId,
  ageCategory = 'U15',
}) => {
  const listScrollRef = useRef<HTMLDivElement>(null);

  const sectionVideos = useMemo(() => {
    if (!contest) return [];
    return getMediaForSection(contest, activeSectionKey);
  }, [contest, activeSectionKey]);

  useEffect(() => {
    listScrollRef.current?.scrollTo({ top: 0 });
  }, [contest?.id, activeSectionKey]);

  const sectionMajor = contest ? getFrame2SectionVisibleIndex(contest, activeSectionKey) : 0;
  const sectionLabel =
    getVisibleFrame2Sections(contest).find((s) => s.key === activeSectionKey)?.label ||
    CONTEST_SECTION_ORDER.find((s) => s.key === activeSectionKey)?.label ||
    'Resources';

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', bgcolor: brandColors.ice }}>
      {contest ? (
        <Box sx={{ px: 1.25, py: 0.85, bgcolor: brandColors.white, borderBottom: '1px solid #e2e8f0', flexShrink: 0 }}>
          <Typography variant="caption" sx={{ color: brandColors.slate, fontSize: '0.75rem', fontWeight: 600 }}>
            {formatContestVideoNumber(sectionMajor)} {sectionLabel} · {sectionVideos.length} video{sectionVideos.length === 1 ? '' : 's'} · age {ageCategory}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ px: 1.25, py: 1, bgcolor: '#fff', borderBottom: '1px solid #e2e8f0' }}>
          <Typography variant="caption" sx={{ color: '#64748b' }}>
            Select a contest to load videos.
          </Typography>
        </Box>
      )}

      <Box
        ref={listScrollRef}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          p: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.25,
          '&::-webkit-scrollbar': { width: 6 },
          '&::-webkit-scrollbar-thumb': { bgcolor: '#cbd5e1', borderRadius: 3 },
        }}
      >
        {!contest || sectionVideos.length === 0 ? (
          <Box
            sx={{
              flex: 1,
              minHeight: 140,
              borderRadius: 1.5,
              border: '1px dashed #cbd5e1',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: '#fff',
            }}
          >
            <Typography variant="caption" sx={{ color: '#94a3b8', textAlign: 'center', px: 1.5 }}>
              {!contest ? 'Select a contest to load videos.' : 'No videos for this section.'}
            </Typography>
          </Box>
        ) : (
          sectionVideos.map((video, index) => {
            const numberLabel = formatContestVideoNumber(sectionMajor + index);
            const selected = video.id === activeVideoId;
            const resolved = resolveMediaClipForAge(video, ageCategory);
            const related = resolved.relatedVideos || [];

            return (
              <Box
                key={video.id}
                onClick={() => onSelectVideo(video.id)}
                sx={{
                  borderRadius: 1.5,
                  overflow: 'hidden',
                  border: `1px solid ${selected ? brandColors.blue : '#e2e8f0'}`,
                  bgcolor: brandColors.white,
                  flexShrink: 0,
                  boxShadow: selected ? '0 2px 10px rgba(74,111,235,0.15)' : '0 1px 3px rgba(15,23,42,0.04)',
                }}
              >
                <Box sx={{ position: 'relative' }}>
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 8,
                      left: 8,
                      zIndex: 1,
                      minWidth: 24,
                      height: 24,
                      px: 0.5,
                      borderRadius: '999px',
                      bgcolor: brandColors.blue,
                      color: '#fff',
                      fontWeight: 950,
                      fontSize: '0.72rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 2px 6px rgba(124,58,237,0.35)',
                    }}
                  >
                    {numberLabel}
                  </Box>
                  <ContestEmbeddedVideo url={resolved.url} title={resolved.title} />
                </Box>

                <Box sx={{ px: 1, py: 0.75, borderTop: '1px solid #f1f5f9' }}>
                  <Typography sx={{ color: '#0f172a', fontSize: '0.8rem', fontWeight: 800, lineHeight: 1.3 }}>
                    {numberLabel} {resolved.title}
                  </Typography>
                </Box>

                {related.length > 0 && (
                  <Box sx={{ px: 0.85, pb: 0.85, pt: 0.15, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
                    <Typography
                      sx={{
                        color: '#64748b',
                        fontSize: '0.62rem',
                        fontWeight: 800,
                        letterSpacing: 0.6,
                        textTransform: 'uppercase',
                        mb: 0.6,
                        px: 0.15,
                      }}
                    >
                      Related videos
                    </Typography>
                    <Stack spacing={0.85}>
                      {related.map((rel, relIndex) => {
                        const relatedNumber = formatContestVideoNumber(sectionMajor + index, relIndex);
                        const relClip = resolveMediaClipForAge(
                          {
                            title: rel.title,
                            url: rel.url,
                            notes: rel.notes,
                            byAge: rel.byAge,
                          },
                          ageCategory,
                        );
                        return (
                          <Box
                            key={rel.id}
                            sx={{
                              borderRadius: 1.25,
                              overflow: 'hidden',
                              border: '1px solid #e2e8f0',
                              bgcolor: '#fff',
                            }}
                          >
                            <Box sx={{ position: 'relative' }}>
                              <Box
                                sx={{
                                  position: 'absolute',
                                  top: 6,
                                  left: 6,
                                  zIndex: 1,
                                  px: 0.55,
                                  height: 20,
                                  borderRadius: '999px',
                                  bgcolor: '#a78bfa',
                                  color: '#fff',
                                  fontWeight: 900,
                                  fontSize: '0.62rem',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                {relatedNumber}
                              </Box>
                              <ContestEmbeddedVideo url={relClip.url} title={relClip.title} />
                            </Box>
                            <Box sx={{ px: 0.75, py: 0.55, borderTop: '1px solid #f1f5f9' }}>
                              <Typography sx={{ color: '#1e293b', fontSize: '0.72rem', fontWeight: 700, lineHeight: 1.25 }}>
                                {relatedNumber} {relClip.title}
                              </Typography>
                            </Box>
                          </Box>
                        );
                      })}
                    </Stack>
                  </Box>
                )}
              </Box>
            );
          })
        )}
      </Box>
    </Box>
  );
};

interface ContestTypeTabsProps {
  value: string;
  onChange: (value: string) => void;
  options: { id: string; label: string }[];
  /** Shown in title, e.g. Developmental & Performance Capabilities (18 Skills) */
  skillCount?: number;
}

export const ContestTypeTabs: React.FC<ContestTypeTabsProps> = ({
  value,
  onChange,
  options,
  skillCount,
}) => {
  const countSuffix =
    typeof skillCount === 'number'
      ? ` (${skillCount} Skill${skillCount === 1 ? '' : 's'})`
      : '';

  if (options.length <= 1) {
    const only = options[0];
    return (
      <Box
        sx={{
          px: 1.5,
          py: 1.1,
          bgcolor: '#fff',
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: { xs: '0.92rem', md: '1rem' },
            color: brandColors.navy,
            letterSpacing: 0.2,
            lineHeight: 1.25,
            fontFamily: '"Sora", Arial, sans-serif',
          }}
        >
          {(only?.label || 'Developmental & Performance Capabilities') + countSuffix}
        </Typography>
      </Box>
    );
  }

  return (
    <Tabs
      value={value}
      onChange={(_, next) => onChange(next)}
      variant="fullWidth"
      sx={{
        minHeight: 44,
        bgcolor: '#fff',
        borderBottom: '1px solid #e2e8f0',
        '& .MuiTab-root': {
          textTransform: 'none',
          fontWeight: 800,
          minHeight: 44,
          fontSize: '0.85rem',
          color: '#64748b',
        },
        '& .Mui-selected': { color: `${brandColors.navy} !important` },
        '& .MuiTabs-indicator': { bgcolor: brandColors.navy, height: 3 },
      }}
    >
      {options.map((opt) => (
        <Tab
          key={opt.id}
          value={opt.id}
          label={opt.id === value ? `${opt.label}${countSuffix}` : opt.label}
        />
      ))}
    </Tabs>
  );
};

interface AgeCategoryTabsProps {
  value: string;
  onChange: (value: string) => void;
  categories: readonly string[];
}

export const AgeCategoryTabs: React.FC<AgeCategoryTabsProps> = ({ value, onChange, categories }) => (
  <Tabs
    value={value}
    onChange={(_, next) => onChange(next)}
    variant="scrollable"
    scrollButtons="auto"
    sx={{
      minHeight: 48,
      bgcolor: '#f8fafc',
      borderBottom: '1px solid #e2e8f0',
      '& .MuiTab-root': {
        textTransform: 'none',
        fontWeight: 900,
        minHeight: 48,
        minWidth: 64,
        fontSize: '0.95rem',
        color: '#64748b',
        px: 1.5,
      },
      '& .Mui-selected': { color: `${brandColors.blue} !important` },
      '& .MuiTabs-indicator': { bgcolor: brandColors.blue, height: 3 },
    }}
  >
    {categories.map((age) => (
      <Tab key={age} value={age} label={age} />
    ))}
  </Tabs>
);

interface ContestFrameShellProps {
  frameLabel?: string;
  accent?: string;
  tone?: 'primary' | 'secondary';
  ageCategory?: string;
  /** Highlighted age pill shown beside integrated title (not inline text). */
  ageBadge?: string;
  /** Current contest quarter, shown beside the age pill. */
  quarterBadge?: string;
  headerExtra?: React.ReactNode;
  icon?: React.ReactNode;
  suppressHeader?: boolean;
  /** Title bar integrated inside the same bordered panel (aligned across columns). */
  integratedTitle?: string;
  titleIcon?: React.ReactNode;
  titleAccent?: string;
  frameHeight?: string | Record<string, string | number>;
  children: React.ReactNode;
}

export const ContestFrameShell: React.FC<ContestFrameShellProps> = ({
  frameLabel = '',
  accent,
  tone = 'secondary',
  ageCategory,
  ageBadge,
  quarterBadge,
  headerExtra,
  icon,
  suppressHeader = false,
  integratedTitle,
  titleIcon,
  titleAccent = brandColors.navy,
  frameHeight,
  children,
}) => {
  const isPrimary = tone === 'primary';
  const resolvedHeight = frameHeight ?? {
    xs: 'min(62vh, 520px)',
    md: 'min(68vh, 580px)',
    lg: 'calc(100dvh - 200px)',
  };
  const useIntegratedTitle = Boolean(integratedTitle);

  return (
    <Paper
      elevation={useIntegratedTitle ? 1 : 2}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        flex: 1,
        height: resolvedHeight,
        maxHeight: resolvedHeight,
        minHeight: { xs: 280, lg: 420 },
        minWidth: 0,
        borderRadius: 2.5,
        overflow: 'hidden',
        border: `1px solid ${isPrimary ? `${brandColors.blue}55` : '#e2e8f0'}`,
        bgcolor: isPrimary ? brandColors.ice : brandColors.white,
        boxShadow: useIntegratedTitle
          ? '0 2px 12px rgba(17,23,57,0.07)'
          : isPrimary
            ? '0 6px 24px rgba(17,23,57,0.12)'
            : '0 4px 20px rgba(17,23,57,0.06)',
      }}
    >
      {useIntegratedTitle ? (
        <Box
          sx={{
            px: 1.5,
            py: 1.15,
            minHeight: 48,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexWrap: 'wrap',
            flexShrink: 0,
            bgcolor: brandColors.white,
            borderBottom: `2px solid ${titleAccent}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
            {titleIcon}
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: { xs: '0.88rem', md: '0.95rem' },
                color: brandColors.navy,
                fontFamily: '"Sora", Arial, sans-serif',
                lineHeight: 1.25,
              }}
            >
              {integratedTitle}
            </Typography>
          </Box>
          {(ageBadge || ageCategory) && <ContestAgeBadge age={ageBadge || ageCategory!} />}
          {quarterBadge ? (
            <Chip
              label={quarterBadge}
              size="small"
              sx={{
                height: 28,
                fontWeight: 800,
                fontSize: '0.78rem',
                bgcolor: brandColors.navy,
                color: brandColors.white,
                flexShrink: 0,
                '& .MuiChip-label': { px: 1.2 },
              }}
            />
          ) : null}
        </Box>
      ) : !suppressHeader && frameLabel ? (
        <FrameHeader label={frameLabel} accent={accent} emphasis={isPrimary} ageCategory={ageCategory} icon={icon} />
      ) : null}
      {headerExtra}
      {children}
    </Paper>
  );
};
